import { forwardRef } from 'react';

import {
  useMonkeysBaseAppearance,
  useMonkeysComponentAttributes,
} from '../../provider/context';
import { resolveBaseAppearance, resolveBaseLayoutLength } from './theme';
import type { BaseScrollAreaAxis, BaseScrollAreaProps } from './types';
import { cn } from './utils';

function resolveOverflow(axis: BaseScrollAreaAxis) {
  if (axis === 'x') {
    return { overflowX: 'auto', overflowY: 'hidden' } as const;
  }
  if (axis === 'y') {
    return { overflowX: 'hidden', overflowY: 'auto' } as const;
  }
  if (axis === 'both') {
    return { overflowX: 'auto', overflowY: 'auto' } as const;
  }
  throw new TypeError('BaseScrollArea axis must be "x", "y", or "both".');
}

export const BaseScrollArea = forwardRef<HTMLDivElement, BaseScrollAreaProps>(
  function BaseScrollArea(
    {
      children,
      axis = 'y',
      maxWidth,
      maxHeight,
      appearance,
      className,
      classNames,
      style,
      ...viewportProps
    },
    ref,
  ) {
    const resolvedAppearance = useMonkeysBaseAppearance(appearance);
    const componentAttributes = useMonkeysComponentAttributes();
    const theme = resolveBaseAppearance(resolvedAppearance);
    const overflow = resolveOverflow(axis);
    const resolvedMaxWidth =
      maxWidth === undefined
        ? undefined
        : resolveBaseLayoutLength('BaseScrollArea', 'maxWidth', maxWidth);
    const resolvedMaxHeight =
      maxHeight === undefined
        ? undefined
        : resolveBaseLayoutLength('BaseScrollArea', 'maxHeight', maxHeight);

    return (
      <div
        {...componentAttributes}
        {...viewportProps}
        ref={ref}
        data-monkeys-component="scroll-area"
        data-slot="viewport"
        data-axis={axis}
        className={cn(
          theme.slots.scrollAreaViewport,
          classNames?.viewport,
          className,
        )}
        style={{
          ...theme.styles.scrollArea,
          ...overflow,
          maxWidth: resolvedMaxWidth,
          maxHeight: resolvedMaxHeight,
          ...style,
        }}
      >
        {children}
      </div>
    );
  },
);
